import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import config from '@iotv/config';
import { existsFilter } from '@iotv/core-services';
import { edgeTypeMatrix, EntityProgrammeSet, getNiceDate, isDeviceContext, isEntityProgrammeSetConstructorProps } from '@iotv/datamodel-core';
import { AppValueTypes, getZombieInstanceFromPrimaryKey } from '@iotv/iotv-v3-types';
import { deviceEnabledTypes } from '@iotv/payload-definition';
import SendIcon from '@mui/icons-material/Send';
import { Button, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useRef } from 'react';
import { v4 } from 'uuid';
import { ListerlizerAdapter } from '../../../../../../../components/factories/Listerlizer/legacy/ListerlizerAdapter';
import { AppMainViewContext } from '../../../../../../../ContextProviders/AppContextProvider';
import { NodeEdgeMapperContext } from '../../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { ConsumerDao } from '../../../../../../../data/ConsumerDAO';
import { subscribeToMQTTChannel } from '../../../../../../../data/daoFunctions/daoFunctions';
import { extractViewObjectFromNodeEdgeMapper } from '../../../../../../../data/daoFunctions/nodeEdgeMapperTransform';
import { DeviceControlDAO } from '../../../../../../../data/DeviceControlDAO';
import { isAdminRole } from '../../../../../../../helpers/access';
import { toTitleCase } from '../../../../../../../helpers/format';
import { AdjacentType, ObjectViewType, Severity, } from '../../../../../../../types';
import { DayMaskSummary } from '../../../EntityProgamme/Components/DayMaskSummary';
import { getDataTable } from '../../fns/getDataTable';
import { EntityProgrammeSetContext } from '../../Reducers/EntityProgrammeSetReducer';
import { EntityProgrammeSetTimelineChart } from '../EntityProgrammeSetTimelineChart';
const debug = process.env.REACT_APP_DEBUG && false;
const programmeStartSort = ([skA, prA], [skB, prB]) => {
    const firstA = prA.enabledDateRanges.at(0);
    const firstB = prB.enabledDateRanges.at(0);
    if (firstA && firstB) {
        const startA = firstA.dateRange[0];
        const startB = firstB.dateRange[0];
        return startA < startB ? -1 : 1;
    }
    else
        return 0;
};
const programmeNameSort = ([skA, prA], [skB, prB]) => {
    return prA.programme.name.localeCompare(prB.programme.name);
};
export const EntityProgrammesTab = (props) => {
    const [state, dispatch] = useContext(EntityProgrammeSetContext);
    const { programmeSetLoading, programmesLoading, programmes, programmeSet } = state;
    const { state: { nodeEdgeMapper }, dispatch: nodeEdgeMapperDispatch, } = useContext(NodeEdgeMapperContext);
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { userSelectedGroup, transactionFunctions: TransactionFunctions, userFunctions, user: contextUser } = props;
    const deviceConfigSubRef = useRef(undefined);
    useEffect(() => {
        if (contextUser) {
            const deviceConfigTopic = config.aws.mqtt.deviceConfigUpdateChannel.replace('[deviceSk]', '#');
            deviceConfigSubRef.current = subscribeToMQTTChannel(contextUser, deviceConfigTopic, (data) => {
                const receivedObject = data.value;
                debug && console.log('Got message on device config channel', receivedObject);
                if (isDeviceContext(receivedObject)) {
                    debug && console.log('Updating NEM with deviceContext', receivedObject);
                    const action = {
                        type: 'loadItems',
                        value: { items: [receivedObject], canaryString: `EntityProgrammesTab load updated deviceContext` },
                    };
                    nodeEdgeMapperDispatch(action);
                }
            });
        }
        return () => {
            deviceConfigSubRef.current?.unsubscribe();
        };
    }, [contextUser?.sk]);
    /** this done entirely on aws side */
    const handleCreateProgrammes = async () => {
        if (programmeSet) {
            const { err, data } = await DeviceControlDAO.device.control.programme.sequential.send({ entityProgrammeSet: programmeSet });
            if (err || data) {
                if (err) {
                    console.log(`Err: createProgrammesRes, ${err.message}`);
                    userFunctions.setUserMessage({
                        id: v4(),
                        type: 'MessageBar',
                        label: 'ProgrammeSet sent',
                        content: `Error sending ${programmeSet?.name}: ${err.message}`,
                        severity: Severity.error,
                    });
                }
                if (data) {
                    debug && console.log(`INFO: createProgrammesRes`, data);
                    const { broadcastData, programmeSet } = data;
                    userFunctions.setUserMessage({
                        id: v4(),
                        type: 'SnackBar',
                        label: 'ProgrammeSet sent',
                        content: `Sent ${programmeSet?.name}`,
                        severity: Severity.success,
                    });
                    if (isEntityProgrammeSetConstructorProps(programmeSet)) {
                        const action = {
                            type: 'SetProgrammeSet',
                            value: { programmeSet: new EntityProgrammeSet(programmeSet) },
                        };
                        dispatch(action);
                        debug && console.log(`INFO: disptached programmeSet action`, action);
                    }
                    else {
                        debug && console.log(`WARN: did not send programmeSet action`, programmeSet);
                    }
                }
            }
            else {
                debug && console.log(`WARN: createProgrammes returned neither err nor data`);
            }
        }
    };
    /** Potentially can be extracted as standard mutated transaction functions for NodeEdgeMapper context */
    const mutatedTransactionFunctions = {
        ...TransactionFunctions,
        saveAndLinkObject: async (existingObjectSk, newObject, adjacentFilter, edgeAttributes) => {
            const existingObject = nodeEdgeMapper.getItem(getZombieInstanceFromPrimaryKey(existingObjectSk));
            if (existingObject) {
                const { err, data } = await ConsumerDao.createAndLink({
                    existingObject,
                    newObject,
                    edgeAttributes,
                    edgeTypeId: adjacentFilter.edgeFilter?.edgeTypeId,
                });
                if (err) {
                    userFunctions.setUserMessage({
                        id: v4(),
                        type: 'MessageBar',
                        label: 'EntityProgramme add',
                        content: err.message,
                        severity: Severity.error,
                    });
                }
                if (data) {
                    const [source, edge, target] = data;
                    const items = [source, edge, target].filter(existsFilter);
                    userFunctions.addObjectsToState(items);
                    userFunctions.setUserMessage({
                        id: v4(),
                        type: 'MessageBar',
                        label: 'EntityProgramme add',
                        content: `Added ${target?.name ?? newObject.name}`,
                        severity: Severity.success,
                    });
                    const action = { type: 'loadItems', value: { items, canaryString: 'EntityProgrammesTab save and link' } };
                    nodeEdgeMapperDispatch(action);
                }
            }
            else {
                console.log(`WARN: can't find existing object in nodeEdgeMapper`, existingObjectSk);
            }
        },
        deleteObject: async (object) => {
            const { err, data } = await ConsumerDao.deleteObject(object);
            if (err) {
                userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'EntityProgramme remove',
                    content: err.message,
                    severity: Severity.error,
                });
            }
            if (data) {
                const item = data;
                userFunctions.removeObjectsFromState([item]);
                userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'EntityProgramme remove',
                    content: `Removed ${object.name}`,
                    severity: Severity.success,
                });
                const action = { type: 'removeItems', value: { items: [item] } };
                nodeEdgeMapperDispatch(action);
            }
        },
    };
    const columnVisibilityReferences = {
        dataTableReference: 'ObjectListCard',
        objectTypeId: 'EntityProgramme',
        viewType: ObjectViewType.List,
        externalColumnVisibility: undefined,
    };
    const rows = Object.entries(programmes ?? {})
        .sort(programmeNameSort)
        .map(([sk, pr]) => {
        const { enabledDateRanges, programme } = pr;
        const thisRows = enabledDateRanges.map((dr) => {
            const { enabled, dateRange } = dr;
            const color = enabled ? 'lightgreen' : 'lightgray';
            const thisRow = [programme.name, '', color, dateRange[0], dateRange[1]];
            return thisRow;
        });
        return thisRows;
    })
        .flat(1);
    debug && console.log(`EntityProgrammes (EnityProgammeSetTimelineChart)`, { rows });
    const newObjectViewDefinition = {
        name: { key: 'name', label: 'Name', editable: true, type: AppValueTypes.STRING, required: true },
        supportedType: {
            key: 'supportedType',
            label: 'Supported Type',
            defaultValue: programmeSet?.supportedType,
            editable: false,
            required: true,
            type: AppValueTypes.ENUM,
            enumKV: Object.fromEntries(Object.keys(deviceEnabledTypes).map((k) => [k, k])),
        },
    };
    const currentProgrammeCount = Object.keys(programmes ?? {}).length;
    const tableHeight = currentProgrammeCount * 41 + 50;
    const selectorConfigParams = {
        controls: { add: currentProgrammeCount < (programmeSet?.maxProgrammes ?? 5), move: false, delete: true, unlink: false, find: false },
    };
    const schedules = (_jsxs(Stack, { width: '100%', gap: 1, children: [programmeSet ? (_jsxs(Typography, { variant: "h5", children: [programmeSet.name ?? '', " schedules"] })) : (_jsx(Skeleton, { variant: "rounded", height: '2.15em', width: '30em' })), state.programmeSet ? (_jsx(ListerlizerAdapter, { mostProximateCustomer: undefined,
                searchRootObject: undefined,
                contextObject: state.programmeSet,
                adjacentFilter: {
                    adjacentType: AdjacentType.CHILD,
                    objectType: 'EntityProgramme',
                    edgeFilter: {
                        edgeTypeId: edgeTypeMatrix.EntityProgrammeSet.EntityProgramme.programme_set_has_programmes.edgeTypeId,
                    },
                },
                viewObject: extractViewObjectFromNodeEdgeMapper(nodeEdgeMapper, state.programmeSet),
                listConfigParams: { label: 'Programmes' },
                loading: programmesLoading,
                columnVisibilityReferences,
                transactionFunctions: mutatedTransactionFunctions,
                userFunctions,
                selectorConfigParams,
                newObjectViewDefinition,
                initialSort: [{ field: 'name', sort: 'asc' }],
                viewDefinition: {
                    enabled: {
                        key: 'enabled',
                        label: 'Enabled',
                        editable: false,
                        type: AppValueTypes.BOOLEAN,
                    },
                    name: {
                        key: 'name',
                        label: 'Name',
                        editable: false,
                        type: AppValueTypes.STRING,
                    },
                    supportedType: {
                        key: 'supportedType',
                        label: 'Supported Type',
                        editable: false,
                        type: AppValueTypes.STRING,
                        valueGetter: (value) => toTitleCase(value),
                    },
                    dayMask: {
                        key: 'dayMask',
                        label: 'Days',
                        editable: false,
                        type: AppValueTypes.OBJECT,
                        renderCell: ({ value }) => _jsx(DayMaskSummary, { dayMask: value }),
                    },
                } })) : (_jsx(Skeleton, { variant: "rounded", height: '10em', width: '25em' })), programmeSetLoading || programmesLoading ? (_jsx(Skeleton, { variant: "rounded", width: '100%', height: `${tableHeight}px` })) : (_jsx(EntityProgrammeSetTimelineChart, { dataTable: getDataTable({ rows }), fragmentProps: { style: { height: tableHeight, width: '100%' } } }))] }));
    const sendButton = (_jsx(Stack, { direction: 'row', justifyContent: 'center', children: _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: 'center', gap: 1, children: [_jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [_jsx(Typography, { fontStyle: 'italic', children: "Last sent:" }), programmeSet ? (_jsx(Typography, { fontStyle: 'italic', children: programmeSet.appliedTimestampsMs && programmeSet.appliedTimestampsMs.length > 0 ? getNiceDate(programmeSet.appliedTimestampsMs.at(0)) : 'Never' })) : (_jsx(Skeleton, { variant: "rounded", height: '1.5em' }))] }), _jsx(Button, { variant: "contained", size: "large", color: "success", endIcon: _jsx(SendIcon, {}), disabled: !isEntityProgrammeSetConstructorProps(programmeSet), onClick: () => handleCreateProgrammes(), children: "Send schedule" })] }) }));
    // const entitiesStatus = (
    //   <>
    //     <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
    //       <Typography variant="h6">{`Scheduled ${pluralize(toTitleCase(programmeSet?.supportedType ?? "Entity"))}`}:</Typography>
    //       <StatusChipsKey />
    //     </Stack>
    //     {programmeSet && scheduledEntities ? <List>{scheduledEntities.map(renderEntityRow)}</List> : <Skeleton variant="rounded" height={"2em"} />}
    //   </>
    // );
    // const groupsStatus = (
    //   <>
    //     <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
    //       <Typography variant="h6">Scheduled {toTitleCase(programmeSet?.supportedType ?? "Entity")} Groups:</Typography>
    //       <StatusChipsKey />
    //     </Stack>
    //     {programmeSet && scheduledGroups ? <List>{scheduledGroups.map(renderGroupRow)}</List> : <Skeleton variant="rounded" height={"2em"} />}
    //   </>
    // );
    return (_jsx(Stack, { gap: 1, children: isAdminRole(userSelectedGroup) ? ([schedules, sendButton]) : (_jsx(Card, { elevation: 3, children: _jsx(CardContent, { sx: { padding: 0, ['&:last-child']: { padding: 2 } }, children: _jsxs(Stack, { gap: 1, children: [schedules, sendButton] }) }) })) }));
};
